import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ComicCard from '@/components/feature/ComicCard';
import { useSEO } from '@/hooks/useSEO';
import { categories } from '@/mocks/categories';
import { getPopularComics, queryComics } from '@/utils/comic';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [keyword, setKeyword] = useState(q);
  const [tag, setTag] = useState('全部');
  const [status, setStatus] = useState('全部');

  const hasKeyword = q.trim().length > 0;

  useSEO(
    hasKeyword ? `搜索“${q}”-韩漫漫画搜索结果` : '漫画搜索-韩漫网址',
    hasKeyword
      ? `韩漫网址「${q}」的搜索结果，可在全部韩漫作品中查找标题、作者相关的漫画，并支持题材与连载状态筛选。`
      : '在韩漫网址搜索韩漫漫画，支持按标题、作者进行关键词检索，快速找到想看的作品。',
    `${q || '韩漫搜索'},韩漫,漫画搜索,韩漫检索`,
  );

  const results = useMemo(
    () => (hasKeyword ? queryComics({ keyword: q, tag, status }) : []),
    [q, hasKeyword, tag, status],
  );

  const hot = useMemo(() => getPopularComics(5), []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = keyword.trim();
    setSearchParams(next ? { q: next } : {});
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {hasKeyword ? `“${q}” 的搜索结果` : '漫画搜索'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {hasKeyword
              ? `共找到 ${results.length} 部相关作品`
              : '输入漫画名称或作者关键词，快速定位你想看的作品'}
          </p>

          <form onSubmit={onSubmit} className="mt-5">
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 focus-within:border-primary-400">
                <i className="ri-search-line text-sm text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="例如：契约恋爱 / 白露 / 奇幻"
                  className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>
        </div>
      </div>

      {hasKeyword ? (
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <div className="mb-6 flex flex-wrap items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">题材</span>
              {[{ key: '全部', label: '全部' }, ...categories.map((c) => ({ key: c.key, label: c.name }))].map(
                (o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setTag(o.key)}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                      tag === o.key
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {o.label}
                  </button>
                ),
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              <span className="text-xs text-foreground-500">状态</span>
              {['全部', '连载中', '已完结'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors cursor-pointer ${
                    status === s
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {results.map((c) => (
                <ComicCard key={c.id} comic={c} showDescription />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-foreground-900">
                没有找到“{q}”相关的漫画
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                换个关键词试试，或从下面的热门推荐开始探索
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams({});
                  setTag('全部');
                  setStatus('全部');
                }}
                className="mt-5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                清空搜索
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <div className="mb-6">
            <h2 className="text-lg font-bold text-foreground-950">热门搜索</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {['契约恋爱', '重生', '奇幻冒险', '校园', '悬疑', '古风', '追妻'].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setKeyword(k);
                    setSearchParams({ q: k });
                  }}
                  className="whitespace-nowrap rounded-full bg-background-100 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          <h2 className="mb-5 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500" />
            大家都在看
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {hot.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700"
            >
              浏览全部漫画
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}